import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { getDemoSetting, renderOpeningLine, OPENING_HEADLINE_DEFAULT, OPENING_SUBLINE_DEFAULT } from '@/lib/demo';
import { CurtainFace, fetchCurtainMedia } from '@/components/admin/ScreenCurtain';

// ── Opening slide ────────────────────────────────────────────────────────────
// The room's first slide: the join QR wrapped in the deck's problem-first
// voice, personalised to the prospect. Shown while the session is in the
// LOBBY — by the projector window AND the mirrored backdrop, so both setups
// open the same way. The personalised curtain film (or still) plays behind a
// scrim when one exists; the company overlay is suppressed because this slide
// carries its own "Prepared for" treatment around the QR.
//
// Attendees who can't scan type the short code into the crew login page
// (the code input shows there whenever demo mode is on) — the join flow,
// NDA and auto sign-in are identical from either door.
const OpeningSlide = ({ session }) => {
  const [media, setMedia] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [headline, setHeadline] = useState(null);
  const [subline, setSubline] = useState(null);

  const joinUrl = session?.code ? `${window.location.origin}/demo?s=${session.code}` : null;

  useEffect(() => {
    let stale = false;
    fetchCurtainMedia().then((m) => { if (!stale) setMedia(m); });
    getDemoSetting('opening_headline').then((v) => { if (!stale) setHeadline(v); });
    getDemoSetting('opening_subline').then((v) => { if (!stale) setSubline(v); });
    return () => { stale = true; };
  }, [session?.id]);

  useEffect(() => {
    if (!joinUrl) { setQrDataUrl(null); return; }
    QRCode.toDataURL(joinUrl, { width: 800, margin: 1, errorCorrectionLevel: 'M' })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [joinUrl]);

  const title = session?.title?.trim() || '';

  return (
    <div className="absolute inset-0 overflow-hidden bg-slate-950 text-white">
      {/* Personalised backdrop, dimmed so the QR owns the slide. */}
      {media && (media.video || media.still) && (
        <div className="absolute inset-0 opacity-40" aria-hidden="true">
          <CurtainFace media={{ ...media, company: null }} />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/40 to-slate-950/80" aria-hidden="true" />

      <div className="relative z-10 flex h-full flex-col items-center justify-center gap-6 p-10 text-center">
        {title && (
          <p className="text-[13px] font-semibold uppercase tracking-[0.4em]" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Prepared for <span style={{ color: '#ffffff' }}>{title}</span>
          </p>
        )}
        <h1 className="max-w-4xl text-3xl font-semibold leading-tight sm:text-5xl" style={{ color: '#ffffff', textShadow: '0 4px 34px rgba(0,0,0,0.65)' }}>
          {renderOpeningLine(headline, OPENING_HEADLINE_DEFAULT, title)}
        </h1>
        <p className="max-w-2xl text-xl leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
          {renderOpeningLine(subline, OPENING_SUBLINE_DEFAULT, title)}
        </p>
        {qrDataUrl && (
          <img
            src={qrDataUrl}
            alt={`QR code to join demo session ${session?.code || ''}`}
            className="w-[min(42vh,70vw)] rounded-3xl bg-white p-4 shadow-2xl"
          />
        )}
        {session?.code && (
          <p className="text-base font-semibold uppercase tracking-[0.25em] text-primary">
            Can&rsquo;t scan? Enter code <span className="rounded-lg bg-white/10 px-3 py-1 font-mono text-white">{session.code}</span> on the crew login page
          </p>
        )}
      </div>
    </div>
  );
};

export default OpeningSlide;
